<script setup>
// =============================================================================
// FangateAgentsManagement.vue
//
// Admin-only Agents management page (mirror of the fangate-crm-prototype.html
// pageAgents() function, lines 2776-2865). Renders in the chat area when
// FangateLayout.listView === 'agents' AND the current user is an admin.
//
// Wires to Chatwoot's stock /api/v1/accounts/:id/agents endpoints via the
// existing AgentsAPI wrapper:
//   GET    → list
//   POST   → create + send invite email (handled server-side by AgentBuilder)
//   PUT    → update name/role/availability
//   DELETE → remove from this account
//
// Deferred to Phase 2 (see Task #199): Assignments table per inbox,
// resend-invite endpoint, deactivate flag.
// =============================================================================

import { ref, reactive, computed, onMounted, watch } from 'vue';
import AgentsAPI from 'dashboard/api/agents';
import InboxMembersAPI from 'dashboard/api/inboxMembers';
import InboxesAPI from 'dashboard/api/inboxes';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { useFangateConfirm } from 'dashboard/composables/useFangateConfirm';
import { useStore } from 'vuex';

const { isAdmin } = useAdmin();
const { confirm } = useFangateConfirm();
const store = useStore();
const currentUserId = computed(() => store.getters.getCurrentUserID);

// All inboxes in the account — used by the edit modal to render the
// inbox-access multi-select. Sourced from Vuex (already loaded by Layout).
const allInboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

// ---- Agent list state -------------------------------------------------------
const agents     = ref([]);
const loading    = ref(false);
const loadError  = ref('');

const loadAgents = async () => {
  loading.value = true;
  loadError.value = '';
  try {
    const resp = await AgentsAPI.get();
    agents.value = resp?.data ?? [];
  } catch (e) {
    loadError.value = e?.response?.data?.message || e?.message || 'Failed to load agents.';
  } finally {
    loading.value = false;
  }
};

// Inbox memberships (inboxId -> [agentId]) for the "Assigned accounts" column.
// Hydrated from InboxMembers on mount + after an edit, so the column reflects
// which chat-accounts each agent is assigned to.
const inboxMembers = reactive({});
const hydrateInboxMembers = async () => {
  await Promise.all((allInboxes.value || []).map(async (ix) => {
    try {
      const resp = await InboxMembersAPI.show(ix.id);
      const members = resp?.data?.payload || resp?.data || [];
      inboxMembers[ix.id] = members.map(m => Number(m.id));
    } catch (_) { /* inbox may have no members yet */ }
  }));
};
const assignedAccounts = agent =>
  (allInboxes.value || []).filter(ix => (inboxMembers[ix.id] || []).includes(Number(agent.id)));

onMounted(async () => {
  await loadAgents();
  hydrateInboxMembers();
});

// ---- View mode: by agent (default) vs by account ---------------------------
// "By agent" = one row per agent, alphabetical. "By account" pivots it: one
// group per chat-account (alphabetical) listing every agent who can reach it —
// so you can read the mapping in both directions.
// Persisted across reloads (same pattern as the panel tabs).
const AGENTS_VIEW_KEY = 'fangate_agents_view';
const readViewMode = () => {
  try { return localStorage.getItem(AGENTS_VIEW_KEY) === 'account' ? 'account' : 'agent'; }
  catch (_) { return 'agent'; }
};
const viewMode = ref(readViewMode());     // 'agent' | 'account'
watch(viewMode, v => { try { localStorage.setItem(AGENTS_VIEW_KEY, v); } catch (_) { /* ignore */ } });
const byName = (a, b) =>
  (a?.name || a?.email || '').localeCompare(b?.name || b?.email || '', undefined, { sensitivity: 'base' });

const sortedAgents = computed(() => [...agents.value].sort(byName));

// Admins reach every account, so they're NOT listed per-account (noise) — the
// caption states it once. Only explicitly-assigned (non-admin) agents show here.
const accountGroups = computed(() => {
  const inboxes = [...(allInboxes.value || [])]
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  return inboxes.map(ix => {
    const memberIds = inboxMembers[ix.id] || [];
    const assigned = agents.value.filter(
      a => a.role !== 'administrator' && memberIds.includes(Number(a.id))
    );
    return { inbox: ix, agents: assigned.sort(byName) };
  });
});

// ---- Invite modal -----------------------------------------------------------
const inviteOpen   = ref(false);
const inviteForm   = reactive({ name: '', email: '', role: 'agent' });
const inviteSaving = ref(false);
const inviteError  = ref('');

const openInvite = () => {
  inviteForm.name  = '';
  inviteForm.email = '';
  inviteForm.role  = 'agent';
  inviteError.value = '';
  inviteOpen.value = true;
};
const closeInvite = () => { inviteOpen.value = false; };

const submitInvite = async () => {
  inviteError.value = '';
  const email = inviteForm.email.trim();
  const name  = inviteForm.name.trim();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    inviteError.value = 'Please enter a valid email address.'; return;
  }
  if (!name) { inviteError.value = 'Name is required.'; return; }

  inviteSaving.value = true;
  try {
    // Chatwoot's create endpoint expects { agent: {...} } shape.
    await AgentsAPI.create({ agent: { email, name, role: inviteForm.role } });
    closeInvite();
    await loadAgents();
  } catch (e) {
    inviteError.value = e?.response?.data?.message
                       || e?.response?.data?.error
                       || 'Invite failed.';
  } finally {
    inviteSaving.value = false;
  }
};

// ---- Edit-Agent modal -------------------------------------------------------
// The kebab now opens this single edit modal that bundles name/role/inbox-
// assignment/activate/delete — no more direct Promote/Remove buttons in the
// menu, so every change goes through the same Save-button discipline.
const editOpen   = ref(false);
const editing    = ref(null);                 // the agent being edited
const editForm   = reactive({
  name: '', role: 'agent', deactivated: false, inboxIds: [],
});
const editInboxesInitial = ref(new Set());   // for delta-detection on save
const editSaving = ref(false);
const editError  = ref('');

const openEditAgent = async (agent) => {
  editing.value = agent;
  editForm.name        = agent.name || '';
  editForm.role        = agent.role || 'agent';
  editForm.deactivated = !!agent.deactivated;
  editError.value      = '';

  // Pull inbox memberships for this agent. The InboxMembers API lists members
  // per inbox, so we iterate inboxes and collect those that include this agent.
  // Cheap enough at admin-time scale.
  const assigned = [];
  await Promise.all(allInboxes.value.map(async (ix) => {
    try {
      const resp = await InboxMembersAPI.show(ix.id);
      const members = resp?.data?.payload || resp?.data || [];
      if (members.some(m => Number(m.id) === Number(agent.id))) assigned.push(Number(ix.id));
    } catch (_) { /* inbox may have no members yet — skip */ }
  }));
  editForm.inboxIds         = assigned;
  editInboxesInitial.value  = new Set(assigned);
  editOpen.value            = true;
};
const closeEditAgent = () => {
  editOpen.value = false;
  editing.value  = null;
};
const toggleInboxAssign = (id) => {
  const idx = editForm.inboxIds.indexOf(id);
  if (idx === -1) editForm.inboxIds.push(id);
  else editForm.inboxIds.splice(idx, 1);
};

const saveEditAgent = async () => {
  const a = editing.value;
  if (!a) return;
  editError.value = '';
  if (!editForm.name.trim()) { editError.value = 'Name is required.'; return; }
  editSaving.value = true;
  try {
    // 1. Patch the agent itself — name + role + deactivated (#199). The
    //    deactivated flag lands on the agent's account_user membership
    //    (AgentsController applies it to current_account_user).
    await AgentsAPI.update(a.id, {
      agent: {
        name: editForm.name.trim(),
        role: editForm.role,
        deactivated: editForm.deactivated,
      },
    });

    // 2. Diff inbox assignments and PATCH the affected inboxes.
    //    InboxMembersAPI.update takes the FULL new agent list for an inbox,
    //    so for each inbox where membership changed we re-fetch + adjust + save.
    const targetSet  = new Set(editForm.inboxIds.map(Number));
    const initialSet = editInboxesInitial.value;
    const changedInboxes = [...new Set([...targetSet, ...initialSet])]
      .filter(id => targetSet.has(id) !== initialSet.has(id));

    for (const inboxId of changedInboxes) {
      const resp     = await InboxMembersAPI.show(inboxId);
      const current  = (resp?.data?.payload || resp?.data || []).map(m => Number(m.id));
      const shouldHave = targetSet.has(Number(inboxId));
      const next = shouldHave
        ? [...new Set([...current, Number(a.id)])]
        : current.filter(id => Number(id) !== Number(a.id));
      await InboxMembersAPI.update({ inboxId: Number(inboxId), agentList: next });
    }

    closeEditAgent();
    await loadAgents();
    hydrateInboxMembers();
  } catch (e) {
    editError.value = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Save failed.';
  } finally {
    editSaving.value = false;
  }
};

const deleteAgentFromModal = async () => {
  const a = editing.value;
  if (!a) return;
  const ok = await confirm({
    title: 'Remove agent?',
    message: `Remove ${a.name} (${a.email}) from this account? This cannot be undone.`,
    confirmLabel: 'Remove',
    danger: true,
  });
  if (!ok) return;
  try {
    await AgentsAPI.delete(a.id);
    closeEditAgent();
    await loadAgents();
  } catch (e) {
    useAlert(e?.response?.data?.message || e?.message || 'Failed to delete agent.');
  }
};

// ---- Assign-agents-to-account modal -----------------------------------------
// Inverse of the edit-agent modal: pick which agents may handle a chat-account
// (= inbox membership). Admins reach every account regardless, so they aren't
// shown as toggles — only their existing memberships are preserved on save.
const acctModalOpen = ref(false);
const acctEditing   = ref(null);                  // the inbox/account being managed
const acctForm      = reactive({ agentIds: [] }); // selected non-admin agent ids
const acctSaving    = ref(false);
const acctError     = ref('');

const isNonAdminId = (id) => {
  const a = agents.value.find(x => Number(x.id) === Number(id));
  return !!a && a.role !== 'administrator';
};
// Only non-admins are assignable; admins always have access to every account.
const assignableAgents = computed(() =>
  sortedAgents.value.filter(a => a.role !== 'administrator')
);

const openAcctAgents = async (inbox) => {
  acctEditing.value   = inbox;
  acctError.value     = '';
  // Seed from the hydrated cache for an instant render…
  acctForm.agentIds   = (inboxMembers[inbox.id] || []).map(Number).filter(isNonAdminId);
  acctModalOpen.value = true;
  // …then refresh this one inbox's members from the server (cheap, authoritative).
  try {
    const resp    = await InboxMembersAPI.show(inbox.id);
    const members = (resp?.data?.payload || resp?.data || []).map(m => Number(m.id));
    inboxMembers[inbox.id] = members;
    acctForm.agentIds      = members.filter(isNonAdminId);
  } catch (_) { /* keep the cached selection */ }
};
const closeAcctAgents = () => { acctModalOpen.value = false; acctEditing.value = null; };
const toggleAgentAssign = (agentId) => {
  const id  = Number(agentId);
  const idx = acctForm.agentIds.indexOf(id);
  if (idx === -1) acctForm.agentIds.push(id);
  else acctForm.agentIds.splice(idx, 1);
};

const saveAcctAgents = async () => {
  const ix = acctEditing.value;
  if (!ix) return;
  acctError.value  = '';
  acctSaving.value = true;
  try {
    // Preserve any ADMIN memberships already on the inbox (not shown as toggles)
    // and replace the non-admin set with the current selection. InboxMembers.update
    // takes the FULL member list for the inbox.
    const existing       = (inboxMembers[ix.id] || []).map(Number);
    const adminMemberIds = existing.filter(id => !isNonAdminId(id));
    const next = [...new Set([...adminMemberIds, ...acctForm.agentIds.map(Number)])];
    await InboxMembersAPI.update({ inboxId: Number(ix.id), agentList: next });
    closeAcctAgents();
    hydrateInboxMembers();
  } catch (e) {
    acctError.value = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Save failed.';
  } finally {
    acctSaving.value = false;
  }
};

// ---- Action menu (now only opens the edit modal) ----------------------------
const actionMenu = reactive({ open: false, agentId: null, x: 0, y: 0 });
const openActionMenu = (e, agent) => {
  e.stopPropagation();
  const rect = e.currentTarget.getBoundingClientRect();
  actionMenu.open    = true;
  actionMenu.agentId = agent.id;
  actionMenu.x       = rect.right - 150;
  actionMenu.y       = rect.bottom + 6;
};
const closeActionMenu = () => { actionMenu.open = false; actionMenu.agentId = null; };
const actionMenuAgent = computed(() => agents.value.find(a => a.id === actionMenu.agentId));
const openEditFromMenu = () => {
  const a = actionMenuAgent.value;
  closeActionMenu();
  if (a) openEditAgent(a);
};

// Re-send the invitation email to a pending (unconfirmed) agent.
const resendInviteFromMenu = async () => {
  const a = actionMenuAgent.value;
  closeActionMenu();
  if (!a) return;
  try {
    await AgentsAPI.resendInvitation(a.id);
    useAlert(`Invitation email re-sent to ${a.email}.`);
  } catch (_) {
    useAlert('Could not resend the invitation — please try again.');
  }
};

// ---- Helpers ----------------------------------------------------------------
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
};
const isOnline = (a) => a.availability_status === 'online';
const isBusy   = (a) => a.availability_status === 'busy';

// Outside-click closer for the actions menu.
const handleDocClick = () => { if (actionMenu.open) closeActionMenu(); };
onMounted(() => document.addEventListener('click', handleDocClick));
</script>

<template>
  <!-- Non-admin lock — shouldn't happen via the sidebar (it's hidden) but a
       sneaky URL/state still lands here, so gate the actual content too. -->
  <div v-if="!isAdmin" class="fa-locked">
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" stroke-width="1.5">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
    <h2>Admins only</h2>
    <p>Agent management is restricted to administrators.</p>
  </div>

  <div v-else class="fa-page">
    <!-- Header -->
    <div class="fa-head">
      <div>
        <h1 class="fa-h1">Agents</h1>
        <p class="fa-sub">
          {{ agents.length }} member{{ agents.length === 1 ? '' : 's' }} ·
          Only admins can invite and manage agents
        </p>
      </div>
      <button class="fa-invite-btn" @click="openInvite">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="12" y1="5"  x2="12" y2="19"/>
          <line x1="5"  y1="12" x2="19" y2="12"/>
        </svg>
        Invite Agent
      </button>
    </div>

    <!-- Full-width permissions note (moved out of the flex header so it spans the
         whole page instead of being squeezed into the title column). -->
    <p class="fa-perms-note">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      <span>Agents only see the chat accounts &amp; Fangate vaults assigned to them — never Chat-Account settings, Agents performance, or Custom reports, and no reports/analytics for accounts they aren't assigned to.</span>
    </p>

    <div v-if="loadError" class="fa-error">{{ loadError }}</div>

    <!-- View toggle: by agent (default) vs by account -->
    <div class="fa-viewtoggle">
      <button class="fa-vt-btn" :class="{ active: viewMode === 'agent' }"
              @click="viewMode = 'agent'">Sort by Agent</button>
      <button class="fa-vt-btn" :class="{ active: viewMode === 'account' }"
              @click="viewMode = 'account'">Sort by Account</button>
    </div>

    <!-- Agents table (by agent, alphabetical) -->
    <div v-if="viewMode === 'agent'" class="fa-table">
      <div class="fa-thead fa-grid">
        <div>Name</div>
        <div>E-Mail</div>
        <div>Assigned accounts</div>
        <div>Role</div>
        <div>Status</div>
        <div>Invite</div>
        <div></div>
      </div>

      <div v-if="loading && !agents.length" class="fa-empty">Loading…</div>
      <div v-else-if="!agents.length" class="fa-empty">
        No agents in this account yet.
      </div>

      <div v-for="a in sortedAgents" :key="a.id" class="fa-row fa-grid"
           :class="{ 'fa-row--deactivated': a.deactivated }">
        <!-- Name + avatar + "you" badge for current user -->
        <div class="fa-cell-name">
          <div class="fa-avatar">
            <img v-if="a.thumbnail" :src="a.thumbnail" :alt="a.name" />
            <span v-else>{{ getInitials(a.name || a.email) }}</span>
          </div>
          <div>
            <div class="fa-name">
              {{ a.name || '—' }}
              <span v-if="a.id === currentUserId" class="fa-self-badge">You</span>
            </div>
          </div>
        </div>

        <!-- Email -->
        <div class="fa-email" :title="a.email">{{ a.email }}</div>

        <!-- Assigned accounts (inbox memberships). Admins always have access to
             every account, so listing individual assignments would be noise. -->
        <div class="fa-accounts">
          <span v-if="a.role === 'administrator'" class="fa-acct-chip"
                title="Admins have access to all accounts">All accounts</span>
          <template v-else-if="assignedAccounts(a).length">
            <span v-for="ix in assignedAccounts(a)" :key="ix.id" class="fa-acct-chip"
                  :title="ix.name">{{ ix.name }}</span>
          </template>
          <span v-else class="fa-acct-none">—</span>
        </div>

        <!-- Role badge -->
        <div class="fa-cell-role">
          <span class="fa-pill"
                :class="a.role === 'administrator' ? 'fa-pill--admin' : 'fa-pill--agent'">
            {{ a.role === 'administrator' ? 'Admin' : 'Agent' }}
          </span>
        </div>

        <!-- Availability / deactivated (#199) -->
        <div class="fa-status">
          <span v-if="a.deactivated" class="fa-pill fa-pill--deactivated">Deactivated</span>
          <template v-else>
            <span class="fa-status-dot"
                  :class="{
                    'fa-status-dot--on':    isOnline(a),
                    'fa-status-dot--busy':  isBusy(a),
                    'fa-status-dot--off':  !isOnline(a) && !isBusy(a),
                  }"></span>
            <span>{{ isOnline(a) ? 'Online' : isBusy(a) ? 'Busy' : 'Offline' }}</span>
          </template>
        </div>

        <!-- Invite status (confirmed = accepted) -->
        <div class="fa-cell-invite">
          <span v-if="a.confirmed" class="fa-pill fa-pill--verified">✓ Verified</span>
          <span v-else class="fa-pill fa-pill--pending">⏳ Pending</span>
        </div>

        <!-- Actions kebab -->
        <div class="fa-cell-actions">
          <button class="fa-kebab" @click="openActionMenu($event, a)" title="Actions">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5"  r="1.5"/>
              <circle cx="12" cy="12" r="1.5"/>
              <circle cx="12" cy="19" r="1.5"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- By-account view: each chat-account (alphabetical) with the agents
         explicitly assigned to it. Admins reach every account, so they're noted
         once in the caption rather than repeated per group. -->
    <div v-else class="fa-table">
      <div class="fa-acct-caption">
        Administrators have access to all accounts, so they are not listed explicitly.
      </div>
      <div class="fa-acct-thead fa-acct-rowgrid">
        <span>Account</span>
        <span>Agent</span>
        <span>E-Mail</span>
        <span></span>
      </div>
      <div v-if="loading && !allInboxes.length" class="fa-empty">Loading…</div>
      <div v-else-if="!accountGroups.length" class="fa-empty">
        No chat-accounts yet.
      </div>
      <div v-for="g in accountGroups" :key="g.inbox.id" class="fa-acct-group">
        <div class="fa-acct-group-head" role="button" tabindex="0"
             @click="openAcctAgents(g.inbox)"
             @keydown.enter="openAcctAgents(g.inbox)"
             @keydown.space.prevent="openAcctAgents(g.inbox)">
          {{ g.inbox.name }}
          <span class="fa-acct-count">{{ g.agents.length }}</span>
          <span class="fa-acct-count-label">agents assigned to this account</span>
          <span class="fa-acct-manage">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Manage
          </span>
        </div>
        <div v-if="!g.agents.length" class="fa-acct-empty">No agents assigned.</div>
        <div v-for="a in g.agents" :key="g.inbox.id + '-' + a.id" class="fa-acct-agent fa-acct-rowgrid">
          <span></span>
          <div class="fa-acct-agent-id">
            <div class="fa-avatar fa-avatar--sm">
              <img v-if="a.thumbnail" :src="a.thumbnail" :alt="a.name" />
              <span v-else>{{ getInitials(a.name || a.email) }}</span>
            </div>
            <span class="fa-acct-agent-name">{{ a.name || a.email }}</span>
          </div>
          <span class="fa-acct-agent-email" :title="a.email">{{ a.email }}</span>
          <button class="fa-kebab" @click="openActionMenu($event, a)" title="Actions">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5"  r="1.5"/>
              <circle cx="12" cy="12" r="1.5"/>
              <circle cx="12" cy="19" r="1.5"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Floating per-agent actions menu — now only opens the edit modal so
         every change (role, inbox access, delete) goes through one disciplined
         Save flow instead of one-click destructive shortcuts. -->
    <div v-if="actionMenu.open" class="fa-action-menu"
         :style="{ left: actionMenu.x + 'px', top: actionMenu.y + 'px' }"
         @click.stop>
      <div class="fa-action-head">
        {{ actionMenuAgent?.name || actionMenuAgent?.email || 'Agent' }}
      </div>
      <button class="fa-action-item" @click="openEditFromMenu">
        ✏️ Edit agent…
      </button>
      <!-- Only useful while the invite is still pending -->
      <button v-if="actionMenuAgent && !actionMenuAgent.confirmed"
              class="fa-action-item" @click="resendInviteFromMenu">
        ✉️ Resend invitation
      </button>
    </div>

    <!-- ===== Edit Agent modal — bundles name/role/inbox access/delete ===== -->
    <div v-if="editOpen" class="fa-modal-overlay" @click.self="closeEditAgent">
      <div class="fa-modal fa-edit-modal">
        <div class="fa-modal-head">
          <h3>Edit agent</h3>
          <button class="fa-modal-close" @click="closeEditAgent">×</button>
        </div>
        <div class="fa-modal-body">
          <!-- Identity row: avatar + email (RO) -->
          <div class="fa-edit-id">
            <div class="fa-avatar">
              <img v-if="editing?.thumbnail" :src="editing.thumbnail" :alt="editing.name" />
              <span v-else>{{ getInitials(editing?.name || editing?.email) }}</span>
            </div>
            <div>
              <div class="fa-edit-email">{{ editing?.email }}</div>
              <div class="fa-edit-sub">Email can't be changed here.</div>
            </div>
          </div>

          <!-- Name -->
          <label class="fa-field">
            <span>Name</span>
            <input v-model="editForm.name" type="text" />
          </label>

          <!-- Role -->
          <label class="fa-field">
            <span>Role</span>
            <select v-model="editForm.role"
                    :disabled="editing?.id === currentUserId">
              <option value="agent">Agent — can handle conversations</option>
              <option value="administrator">Administrator — full access</option>
            </select>
            <small v-if="editing?.id === currentUserId" class="fa-hint">
              You can't change your own role from here.
            </small>
          </label>

          <!-- Inbox access -->
          <div class="fa-field">
            <span>Inbox access</span>
            <div v-if="!allInboxes.length" class="fa-hint">
              No inboxes configured yet.
            </div>
            <div v-else class="fa-inbox-grid">
              <button v-for="ix in allInboxes" :key="ix.id"
                      type="button"
                      class="fa-inbox-chip"
                      :class="{ 'is-on': editForm.inboxIds.includes(ix.id) }"
                      @click="toggleInboxAssign(ix.id)">
                <span class="fa-inbox-chip-dot"></span>
                {{ ix.name }}
              </button>
            </div>
            <small class="fa-hint">
              Pick the Telegram inboxes this agent should be able to handle.
              Chatwoot policies block conversations from inboxes they're not
              a member of.
            </small>
          </div>

          <!-- Activate / Deactivate (#199) -->
          <div class="fa-field">
            <span>Status</span>
            <label class="fa-inline-check">
              <input type="checkbox" v-model="editForm.deactivated"
                     :disabled="editing?.id === currentUserId" />
              <span>Deactivate this agent</span>
            </label>
            <small class="fa-hint">
              Deactivated agents stay in the table (greyed out) and keep their
              current conversations, but can't access the workspace or receive
              new assignments.
              <template v-if="editing?.id === currentUserId">
                You can't deactivate your own account.
              </template>
            </small>
          </div>

          <div v-if="editError" class="fa-error">{{ editError }}</div>
        </div>
        <div class="fa-modal-foot fa-edit-foot">
          <!-- Destructive action on the left — visual separation from Save -->
          <button class="fa-btn-danger"
                  :disabled="editing?.id === currentUserId"
                  @click="deleteAgentFromModal">
            🗑️ Remove from account
          </button>
          <div style="display:flex; gap:8px;">
            <button class="fa-btn-secondary" @click="closeEditAgent">Cancel</button>
            <button class="fa-btn-primary"
                    :disabled="editSaving"
                    @click="saveEditAgent">
              {{ editSaving ? 'Saving…' : 'Save changes' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Assign-agents-to-account modal — toggle inbox membership ===== -->
    <div v-if="acctModalOpen" class="fa-modal-overlay" @click.self="closeAcctAgents">
      <div class="fa-modal fa-acct-modal">
        <div class="fa-modal-head">
          <h3>Assign agents</h3>
          <button class="fa-modal-close" @click="closeAcctAgents">×</button>
        </div>
        <div class="fa-modal-body">
          <!-- Account identity -->
          <div class="fa-edit-id">
            <div class="fa-avatar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
              </svg>
            </div>
            <div>
              <div class="fa-edit-email">{{ acctEditing?.name }}</div>
              <div class="fa-edit-sub">Choose which agents can handle this chat account.</div>
            </div>
          </div>

          <div v-if="!assignableAgents.length" class="fa-hint">
            No agents to assign yet — invite agents first.
          </div>
          <div v-else class="fa-agent-list">
            <button v-for="a in assignableAgents" :key="a.id" type="button"
                    class="fa-agent-row"
                    :class="{ 'is-on': acctForm.agentIds.includes(Number(a.id)) }"
                    @click="toggleAgentAssign(a.id)">
              <div class="fa-avatar fa-avatar--sm">
                <img v-if="a.thumbnail" :src="a.thumbnail" :alt="a.name" />
                <span v-else>{{ getInitials(a.name || a.email) }}</span>
              </div>
              <div class="fa-agent-row-id">
                <span class="fa-agent-row-name">{{ a.name || a.email }}</span>
                <span class="fa-agent-row-email" :title="a.email">{{ a.email }}</span>
              </div>
              <span class="fa-agent-row-check" aria-hidden="true">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
            </button>
          </div>

          <small class="fa-hint">
            Administrators can reach every account automatically, so they aren't listed here.
          </small>

          <div v-if="acctError" class="fa-error">{{ acctError }}</div>
        </div>
        <div class="fa-modal-foot">
          <button class="fa-btn-secondary" @click="closeAcctAgents">Cancel</button>
          <button class="fa-btn-primary" :disabled="acctSaving" @click="saveAcctAgents">
            {{ acctSaving ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Invite modal -->
    <div v-if="inviteOpen" class="fa-modal-overlay" @click.self="closeInvite">
      <div class="fa-modal">
        <div class="fa-modal-head">
          <h3>Invite a new agent</h3>
          <button class="fa-modal-close" @click="closeInvite">×</button>
        </div>
        <div class="fa-modal-body">
          <label class="fa-field">
            <span>Name</span>
            <input v-model="inviteForm.name" type="text" placeholder="Maria Müller" />
          </label>
          <label class="fa-field">
            <span>E-Mail</span>
            <input v-model="inviteForm.email" type="email" placeholder="agent@example.com" />
          </label>
          <label class="fa-field">
            <span>Role</span>
            <select v-model="inviteForm.role">
              <option value="agent">Agent — can handle conversations</option>
              <option value="administrator">Administrator — full access</option>
            </select>
          </label>
          <p class="fa-hint">
            Chatwoot sends an invite e-mail with a sign-up link. The agent
            shows up as <strong>Pending</strong> until they confirm.
          </p>
          <div v-if="inviteError" class="fa-error">{{ inviteError }}</div>
        </div>
        <div class="fa-modal-foot">
          <button class="fa-btn-secondary" @click="closeInvite">Cancel</button>
          <button class="fa-btn-primary"
                  :disabled="inviteSaving"
                  @click="submitInvite">
            {{ inviteSaving ? 'Sending…' : 'Send invite' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.fa-page {
  max-width: 980px;
  padding: 28px 32px;
  font-family: 'Inter', sans-serif;
}
// Mobile (≤768px): cream page background to match the Labels list + the other
// rail sections; tighter padding. (Table → cards is a separate task.)
@media (max-width: 768px) {
  .fa-page { background: var(--bg-middle); padding: 16px; }

  // By-agent table → stacked cards (the 7-col grid is far too wide for a phone).
  // Hide the header row; each row becomes a card with the name as the title,
  // kebab top-right, and labelled detail rows. (.fa-row.fa-grid / .fa-thead.fa-grid
  // raise specificity so they beat the base grid display.)
  .fa-thead.fa-grid { display: none; }
  .fa-row.fa-grid {
    position: relative;
    display: flex; flex-direction: column; align-items: stretch;
    gap: 8px; padding: 14px 44px 14px 16px;
  }
  .fa-cell-actions { position: absolute; top: 10px; right: 8px; }
  .fa-email, .fa-accounts, .fa-cell-role, .fa-status, .fa-cell-invite {
    display: flex; flex-wrap: wrap; align-items: center; gap: 6px; min-width: 0;
  }
  .fa-email::before, .fa-accounts::before, .fa-cell-role::before,
  .fa-status::before, .fa-cell-invite::before {
    flex: 0 0 70px;
    font-family: 'Inter', sans-serif; font-size: 9px; font-weight: 700;
    letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-muted);
  }
  .fa-email::before     { content: 'E-Mail'; }
  .fa-accounts::before  { content: 'Accounts'; }
  .fa-cell-role::before { content: 'Role'; }
  .fa-status::before    { content: 'Status'; }
  .fa-cell-invite::before { content: 'Invite'; }

  // By-account view → stack the agent rows (drop the empty Account column;
  // the account name already shows in the group header above).
  .fa-acct-thead.fa-acct-rowgrid { display: none; }
  .fa-acct-agent.fa-acct-rowgrid {
    display: flex; flex-wrap: wrap; align-items: center;
    gap: 4px 10px; padding: 10px 16px 10px 18px;
  }
  .fa-acct-agent.fa-acct-rowgrid > span:first-child { display: none; }

  // Modals: cap height + scroll the body so head/foot stay reachable on a phone.
  .fa-modal { max-height: 88vh; display: flex; flex-direction: column; }
  .fa-modal-head, .fa-modal-foot { flex-shrink: 0; }
  .fa-modal-body { overflow-y: auto; flex: 1 1 auto; min-height: 0; }
  .fa-agent-list { max-height: none; }    // body scrolls instead (no nested scroll)
}
.fa-locked {
  max-width: 480px;
  text-align: center;
  padding: 64px 24px;
  margin: 0 auto;
  color: var(--ink-muted);
  h2 { font-family: 'Libre Baskerville', serif; font-size: 20px; margin: 14px 0 6px; color: var(--ink-strong); }
  p  { font-size: 13px; }
}
.fa-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 22px;
}
.fa-h1 {
  font-family: 'Libre Baskerville', serif;
  font-size: 24px; font-weight: 700;
  color: var(--ink-strong);
  margin: 0 0 4px;
}
.fa-sub {
  font-size: 12.5px;
  color: var(--ink-muted);
  margin: 0;
}
.fa-perms-note {
  display: flex; align-items: flex-start; gap: 7px;
  margin: 9px 0 0; padding: 8px 11px; max-width: 680px;
  background: var(--bg-subtle); border: 1px solid var(--border-soft); border-radius: 9px;
  font-size: 12px; line-height: 1.45; color: var(--ink-muted);
}
.fa-perms-note svg { color: var(--accent-strong); flex-shrink: 0; margin-top: 1px; }
.fa-invite-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px;
  border-radius: 9px;
  border: none;
  background: var(--accent);
  color: #fff;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(13, 148, 136, 0.3);
  flex-shrink: 0;
  &:hover { background: var(--accent-strong); }
}

.fa-table {
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  overflow: hidden;
}
.fa-accounts { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.fa-acct-chip {
  font-size: 11px; line-height: 1.4; padding: 2px 7px; border-radius: 6px;
  background: var(--accent-tint, rgba(13, 148, 136, 0.12));
  color: var(--accent-strong, #0f766e);
  white-space: nowrap; max-width: 140px; overflow: hidden; text-overflow: ellipsis;
}
.fa-acct-none { color: var(--ink-muted, #9aa3a0); }

// View toggle (by agent / by account)
.fa-viewtoggle {
  display: inline-flex; gap: 0; margin-bottom: 14px;
  border: 1px solid var(--border-soft); border-radius: 9px; overflow: hidden;
}
.fa-vt-btn {
  padding: 7px 14px; border: none; background: var(--bg-panel);
  color: var(--ink-medium); font-size: 12.5px; font-weight: 600; cursor: pointer;
  &:not(:last-child) { border-right: 1px solid var(--border-soft); }
  &:hover { background: var(--bg-subtle); }
  &.active { background: var(--accent); color: #fff; }
}

// By-account grouped view
.fa-acct-caption {
  padding: 10px 18px; font-size: 11.5px; font-style: italic;
  color: var(--ink-muted); border-bottom: 1px solid var(--border-soft);
  background: var(--bg-subtle);
}
.fa-acct-group { border-bottom: 1px solid var(--border-soft);
  &:last-of-type { border-bottom: none; } }
// Account row — leftmost column (aligns under the "Account" header), tinted so
// each group reads as a divider.
.fa-acct-group-head {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 18px;
  background: rgba(13, 148, 136, 0.06);
  border-bottom: 1px solid var(--border-soft);
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
  color: var(--ink-strong);
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: rgba(13, 148, 136, 0.12); }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: -2px; }
  &:hover .fa-acct-manage,
  &:focus-visible .fa-acct-manage {
    border-color: var(--accent);
    background: var(--accent-tint);
    color: var(--accent-strong);
  }
}
.fa-acct-manage {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 11px;
  border-radius: 999px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 600;
  letter-spacing: 0; text-transform: none;
  flex-shrink: 0;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  svg { width: 13px; height: 13px; }
}
.fa-acct-count {
  font-family: 'Inter', sans-serif; font-size: 10.5px; font-weight: 700;
  padding: 1px 7px; border-radius: 999px;
  background: var(--accent-tint, rgba(13, 148, 136, 0.12));
  color: var(--accent-strong, #0f766e);
}
.fa-acct-count-label {
  font-family: 'Inter', sans-serif; font-size: 10.5px; font-weight: 400;
  text-transform: none; letter-spacing: 0; font-style: italic;
  color: var(--ink-muted);
}
.fa-acct-empty {
  padding: 6px 18px; font-size: 12px; font-style: italic; color: var(--ink-muted);
}
// Shared grid for the by-account thead + agent rows: Account | Agent | E-Mail |
// kebab. Account names sit in col1 (under "Account"), agent identity in col2
// (indented, flush under "Agent"), addresses in col3, kebab flush right.
.fa-acct-rowgrid {
  display: grid;
  grid-template-columns: minmax(120px, 0.9fr) minmax(180px, 1.6fr) minmax(150px, 1.5fr) 34px;
  align-items: center; gap: 10px;
}
.fa-acct-thead {
  padding: 9px 18px;
  border-bottom: 1px solid var(--border-soft);
  background: var(--bg-subtle);
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--ink-strong);
}
.fa-acct-agent { padding: 6px 18px; }
.fa-acct-agent-id { display: flex; align-items: center; gap: 9px; min-width: 0; }
.fa-acct-agent-name { font-size: 13px; color: var(--ink-strong); font-weight: 500; }
.fa-acct-agent-email {
  font-size: 12px; color: var(--ink-muted);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fa-avatar--sm { width: 26px; height: 26px; font-size: 10px; }
.fa-grid {
  display: grid;
  grid-template-columns: 2fr 1.8fr 1.6fr 90px 100px 110px 44px;
  align-items: center;
  padding: 11px 18px;
  border-bottom: 1px solid var(--border-soft);
}
.fa-thead {
  background: var(--bg-subtle);
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--ink-strong);
}
.fa-row { &:last-of-type { border-bottom: none; } }
.fa-empty {
  padding: 32px;
  text-align: center;
  color: var(--ink-muted);
  font-style: italic;
  font-size: 12.5px;
}
.fa-cell-name {
  display: flex; align-items: center; gap: 10px;
  min-width: 0;
}
.fa-avatar {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-size: 13px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fa-name {
  font-size: 13px; font-weight: 600;
  color: var(--ink-strong);
}
.fa-self-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--accent);
  color: #fff;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.02em;
}
.fa-email {
  font-size: 12px;
  color: var(--ink-medium);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.fa-pill {
  display: inline-flex; align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.02em;
}
.fa-pill--admin    { background: #fef3c7; color: #92400e; }
.fa-pill--agent    { background: var(--accent-tint); color: var(--accent-strong); }
.fa-pill--verified { background: #d1fae5; color: #065f46; }
.fa-pill--pending  { background: #fef3c7; color: #92400e; }
.fa-pill--deactivated { background: #e5e7eb; color: #6b7280; }
/* #199: deactivated agents stay listed but greyed out */
.fa-row--deactivated { opacity: 0.55; }

.fa-status {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11.5px;
  color: var(--ink-medium);
}
.fa-status-dot {
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  &--on   { background: var(--accent); }
  &--busy { background: #f59e0b; }
  &--off  { background: #cbc7b8; }
}

.fa-cell-actions { display: flex; justify-content: flex-end; }
// Kebab was nearly invisible — transparent over the bone-cream row.
// Give it a default border + slightly stronger ink so the affordance reads
// at a glance, with a clear hover state.
.fa-kebab {
  width: 30px; height: 30px;
  border-radius: 6px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  &:hover {
    background: var(--accent-tint);
    border-color: var(--accent-ring);
    color: var(--accent-strong);
  }
  svg { width: 16px; height: 16px; }
}

.fa-action-menu {
  position: fixed; z-index: 10000;
  min-width: 200px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(31, 42, 42, 0.14);
  padding: 6px;
}
.fa-action-head {
  font-family: 'Inter', sans-serif;
  font-size: 9px; font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--ink-muted);
  padding: 6px 10px 8px;
  border-bottom: 1px solid var(--border-soft);
  margin-bottom: 4px;
}
.fa-action-item {
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-strong);
  text-align: left;
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--bg-subtle); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}
.fa-action-danger { color: #b91c1c; }

.fa-modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(11, 20, 20, 0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.fa-modal {
  background: var(--bg-panel);
  border-radius: 14px;
  width: 100%; max-width: 460px;
  box-shadow: 0 20px 50px rgba(11, 20, 20, 0.25);
  font-family: 'Inter', sans-serif;
}
.fa-modal-head {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--border-soft);
  h3 {
    margin: 0; font-family: 'Libre Baskerville', serif;
    font-size: 16px; font-weight: 700; color: var(--ink-strong);
  }
}
.fa-modal-close {
  width: 28px; height: 28px;
  border: none; background: transparent;
  font-size: 22px; line-height: 1;
  color: var(--ink-muted);
  cursor: pointer; border-radius: 6px;
  &:hover { background: var(--bg-subtle); color: var(--ink-strong); }
}
.fa-modal-body { padding: 16px 20px; }
.fa-modal-foot {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 20px 16px;
  border-top: 1px solid var(--border-soft);
}
.fa-btn-primary, .fa-btn-secondary {
  padding: 8px 16px;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}
.fa-btn-primary {
  background: var(--accent); color: #fff;
  &:hover:not(:disabled) { background: var(--accent-strong); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.fa-btn-secondary {
  background: var(--bg-panel);
  border-color: var(--border-soft);
  color: var(--ink-medium);
  &:hover { background: var(--bg-subtle); color: var(--ink-strong); }
}

.fa-field {
  display: flex; flex-direction: column; gap: 4px;
  margin-bottom: 12px;
  > span {
    font-size: 11px; font-weight: 700;
    color: var(--ink-strong);
    letter-spacing: 0.02em;
  }
  input, select {
    padding: 8px 10px;
    border: 1px solid var(--border-soft);
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    background: var(--bg-panel);
    color: var(--ink-strong);
    &:focus {
      outline: none;
      border-color: var(--accent-strong);
      box-shadow: 0 0 0 2px var(--accent-tint);
    }
  }
}
.fa-hint {
  font-size: 11.5px;
  color: var(--ink-muted);
  margin: 6px 0 0;
  line-height: 1.4;
}
.fa-error {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fee2e2;
  color: #991b1b;
  font-size: 12px;
}

// ---- Edit-Agent modal extras ------------------------------------------------
.fa-edit-modal { max-width: 540px; }
.fa-edit-id {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  margin-bottom: 14px;
}
.fa-edit-email {
  font-size: 13px; font-weight: 600;
  color: var(--ink-strong);
}
.fa-edit-sub {
  font-size: 11px;
  color: var(--ink-muted);
  margin-top: 2px;
}
.fa-inbox-grid {
  display: flex; flex-wrap: wrap; gap: 6px;
  margin-top: 4px;
}
.fa-inbox-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  border: 1.5px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  &:hover {
    border-color: var(--accent);
    color: var(--accent-strong);
  }
  &.is-on {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
    .fa-inbox-chip-dot { background: #fff; }
  }
}
.fa-inbox-chip-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--ink-muted);
}
.fa-inline-check {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 13px;
  color: var(--ink-strong);
  input { width: auto; }
}
.fa-edit-foot {
  justify-content: space-between !important;
}
.fa-btn-danger {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #b91c1c;
    color: #fff;
    border-color: #b91c1c;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// ---- Assign-agents modal: agent toggle rows ---------------------------------
.fa-acct-modal { max-width: 480px; }
.fa-agent-list {
  display: flex; flex-direction: column; gap: 6px;
  margin: 4px 0 10px;
  max-height: 340px; overflow-y: auto;
}
.fa-agent-row {
  display: flex; align-items: center; gap: 10px;
  width: 100%; text-align: left;
  padding: 8px 10px;
  border: 1.5px solid var(--border-soft);
  border-radius: 10px;
  background: var(--bg-panel);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  &:hover { border-color: var(--accent-ring); background: var(--bg-subtle); }
  &.is-on { border-color: var(--accent); background: var(--accent-tint); }
}
.fa-agent-row-id { display: flex; flex-direction: column; min-width: 0; flex: 1; gap: 1px; }
.fa-agent-row-name {
  font-size: 13px; font-weight: 600; color: var(--ink-strong);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fa-agent-row-email {
  font-size: 11.5px; color: var(--ink-muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fa-agent-row-check {
  flex-shrink: 0;
  width: 22px; height: 22px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 1.5px solid var(--border-soft);
  background: var(--bg-panel);
  color: transparent;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.fa-agent-row.is-on .fa-agent-row-check {
  background: var(--accent); border-color: var(--accent); color: #fff;
}
</style>
